import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, ChevronUp, ChevronDown, Loader2,
  CircleCheck, CircleX, CircleAlert,
  FileText, CircleCheckBig, UserPlus, ShieldCheck,
  StickyNote, Tag, Plus, ExternalLink,
} from 'lucide-react';
import { type PipelineCandidate, updateCandidateReview } from '../../services/api';
import { isAcademicDomain } from '../../config/appConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────

function e(rd: Record<string, unknown> | null, key: string): string {
  return String(rd?.[key] ?? '').trim();
}
function eNum(rd: Record<string, unknown> | null, key: string): number | null {
  const v = Number(rd?.[key]);
  return isNaN(v) ? null : v;
}
function isYes(v: string) { return v.toUpperCase() === 'YES'; }
function liIsVerified(v: string) { const s = v.toUpperCase(); return s !== '' && s !== 'NO'; }

function linkedInAccountAgeYears(joinedDate: string): number {
  const d = new Date(joinedDate);
  if (isNaN(d.getTime())) return 0;
  const now = new Date();
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months < 0) months = 0;
  return Math.floor(months / 12);
}

function formatLinkedInAccountAge(joinedDate: string): string {
  const d = new Date(joinedDate);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const joined = d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const ago = years === 0
    ? `${months} month${months === 1 ? '' : 's'} ago`
    : `${years} year${years === 1 ? '' : 's'} ago`;
  return `Joined ${joined} • ${ago}`;
}

// ─── Shared UI ────────────────────────────────────────────────────────────

function StatusBadge({ value, yesLabel = 'Yes', noLabel = 'No', naLabel = "Can't Be Found" }: { value: string; yesLabel?: string; noLabel?: string; naLabel?: string }) {
  const upper = value.toUpperCase();
  if (isYes(upper))
    return <span className="inline-flex h-6 min-w-max items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold leading-none bg-green-100 text-green-800 border-green-200"><CircleCheck className="h-3.5 w-3.5" />{yesLabel}</span>;
  if (upper === 'NO' || upper === 'MISMATCH' || upper === 'FAKE')
    return <span className="inline-flex h-6 min-w-max items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold leading-none bg-red-100 text-red-800 border-red-200"><CircleX className="h-3.5 w-3.5" />{noLabel}</span>;
  if (upper === 'PARTIALLY_FOUND')
    return <span className="inline-flex h-6 min-w-max items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold leading-none bg-orange-100 text-orange-800 border-orange-200"><CircleAlert className="h-3.5 w-3.5" />{naLabel}</span>;
  return <span className="inline-flex h-6 min-w-max items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-semibold leading-none bg-amber-100 text-amber-800 border-amber-200"><CircleAlert className="h-3.5 w-3.5" />{naLabel}</span>;
}

function LinkedText({ text }: { text: string }) {
  const parts = text.split(/(\[.*?\]\(.*?\))/g);
  return (
    <span>
      {parts.map((part, i) => {
        const match = part.match(/^\[(.*?)\]\((.*?)\)$/);
        if (match) return <a key={i} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{match[1]}</a>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
}

function RichText({ text, className = '', maxLength = 400 }: { text: string; className?: string; maxLength?: number }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > maxLength;
  if (!needsTruncation || expanded) {
    const lines = text.split('\n');
    return (
      <div>
        <div className={`text-sm leading-6 ${className}`}>
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              <LinkedText text={line} />
            </React.Fragment>
          ))}
          {needsTruncation && <> <button onClick={(ev) => { ev.stopPropagation(); setExpanded(false); }} className="text-xs text-blue-600 hover:underline font-medium">Show less</button></>}
        </div>
      </div>
    );
  }
  const truncated = text.slice(0, maxLength);
  return (
    <div className={`text-sm leading-6 ${className}`}>
      <LinkedText text={truncated} />… <button onClick={(ev) => { ev.stopPropagation(); setExpanded(true); }} className="text-xs text-blue-600 hover:underline font-medium">See more</button>
    </div>
  );
}

function SignalDot({ color }: { color: 'green' | 'red' | 'amber' }) {
  const cls = color === 'green' ? 'bg-green-500' : color === 'red' ? 'bg-red-500' : 'bg-amber-500';
  return <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${cls}`} />;
}

// ─── Gate Card (collapsed/expanded) ────────────────────────────────────────

type TriPassState = boolean | 'NOT_FOUND' | 'PARTIALLY_FOUND' | null;

function GateCard({ icon, title, passed, summary, reviewCount, children, defaultOpen = false }: {
  icon: React.ReactNode; title: string; passed: TriPassState; summary: string; reviewCount: number; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const badge = passed === true
    ? <StatusBadge value="YES" yesLabel="Passed" />
    : passed === false
      ? <StatusBadge value="NO" noLabel="Failed" />
      : passed === 'NOT_FOUND'
        ? <StatusBadge value="" naLabel="Not found" />
        : passed === 'PARTIALLY_FOUND'
          ? <StatusBadge value="PARTIALLY_FOUND" naLabel="Partially found" />
          : <StatusBadge value="" naLabel="Pending" />;
  const reviewLabel = reviewCount > 0 ? `${reviewCount} to review` : 'No action';

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">{icon}</span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
              {badge}
            </div>
            {summary && <p className="mt-1 text-sm leading-5 text-gray-600">{summary}</p>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">{reviewLabel}</span>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
          >
            Details
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {open && <div className="mt-3 border-t border-gray-100 pt-3">{children}</div>}
    </section>
  );
}

function SubCard({ title, passed, children, defaultOpen }: { title: string; passed: TriPassState; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? passed !== true);
  const badge = passed === true
    ? <StatusBadge value="YES" yesLabel="Passed" />
    : passed === false
      ? <StatusBadge value="NO" noLabel="Failed" />
      : passed === 'NOT_FOUND'
        ? <StatusBadge value="" naLabel="Not found" />
        : passed === 'PARTIALLY_FOUND'
          ? <StatusBadge value="PARTIALLY_FOUND" naLabel="Partially found" />
          : <StatusBadge value="" naLabel="N/A" />;
  const dotColor: 'green' | 'red' | 'amber' = passed === true ? 'green' : passed === false ? 'red' : 'amber';
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-2 text-left">
        <div className="flex min-w-0 items-center gap-2">
          <SignalDot color={dotColor} />
          <h5 className="text-sm font-semibold text-gray-900">{title}</h5>
        </div>
        <div className="flex items-center gap-2">
          {badge}
          {open ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
        </div>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function SourcesCard({ sources }: { sources: { label: string; url: string }[] }) {
  const [open, setOpen] = useState(false);
  // Sources list only carries linked entries — drop URL-less rows.
  // Count must be derived from the rendered list, else the header inflates.
  const linkedSources = sources.filter((src) => src.url);
  const count = linkedSources.length;
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-2 text-left">
        <div className="flex items-center gap-2">
          <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
          <h5 className="text-sm font-semibold text-gray-900">Sources</h5>
          <span className="text-xs text-gray-400">{count}</span>
        </div>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {count === 0 && <p className="text-sm leading-5 text-gray-500">No sources available</p>}
          {linkedSources.map((src, i) => (
            <div key={`s-${i}`} className="flex items-start gap-2 text-sm leading-5">
              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-300" />
              <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{src.label}</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────

export function CandidateDetailModal({ profile, onClose, onApprove, onRemove, loading: actionLoading, embedded }: {
  profile: PipelineCandidate;
  onClose: () => void;
  onApprove: () => void;
  onRemove: () => void;
  loading?: boolean;
  embedded?: boolean;
}) {
  const rd = profile.enrichment ?? {};
  const domain = profile.domain;
  const name = profile.name || e(rd, 'name') || '';
  const isAcademic = isAcademicDomain(domain);

  // ── 1. Internal Resume Review ───────────────────────────────────────
  const resumeScore = eNum(rd, 'BINARY: Local resume check score (NOT cross comparing to external sources)');
  // The local pipeline emits a 0-100 number; the Matching (tpm) API emits a
  // categorical score (REAL / NOT_SURE / INFLATED / FAKE). Capture the raw
  // string so we can render/gate on the category when there is no number.
  const resumeScoreRaw = e(rd, 'BINARY: Local resume check score (NOT cross comparing to external sources)');
  const resumeScoreCategory = resumeScore === null && resumeScoreRaw ? resumeScoreRaw.toUpperCase() : null;
  const resumeVerification = e(rd, 'RESUME VERIFICATION'); // explicit gate (Matching)
  const resumeScoreReasoning = e(rd, 'REASONING: Why resume check score is what it is');
  const redFlagCount = eNum(rd, 'NUMBER: Local resume red flags (NOT cross comparing to external sources)');
  const redFlagReasoning = e(rd, 'REASONING: Why the resume red flags');

  // REAL and CURRENT_ROLE_TO_ADD_TO_RESUME pass the categorical gate (the latter is
  // an authentic resume merely missing the newest role). NOT_SURE / INFLATED / FAKE
  // fail. A non-passing category is NOT rescued by RESUME VERIFICATION=YES (that
  // field tracks EXTERNAL cross-verification and is not authoritative).
  const categoryPass = resumeScoreCategory === 'REAL' || resumeScoreCategory === 'CURRENT_ROLE_TO_ADD_TO_RESUME';
  // Rule (kept in lock-step with the backend deriveResumeVerification / SQL):
  //   1. categorical score (Matching) → REAL / CURRENT_ROLE_TO_ADD_TO_RESUME pass; others fail.
  //   2. else NUMERIC (local, integer OR decimal — eNum parses "100.0") → legacy ≥ 60.
  //   3. else (no score at all) → fall back to the explicit RESUME VERIFICATION gate.
  const resumeScorePass = resumeScoreCategory
    ? categoryPass
    : resumeScore !== null
      ? resumeScore >= 60
      : resumeVerification
        ? isYes(resumeVerification)
        : false;
  const localFlagsPass = redFlagCount !== null && redFlagCount === 0;
  const resumePass = resumeScorePass && localFlagsPass;
  const resumeReviewCount = [!resumeScorePass, !localFlagsPass].filter(Boolean).length;

  // ── 2. Public Source Check ──────────────────────────────────────────
  const agentFinalVerdict = e(rd, 'Agent - Final verdict');
  const agentRedFlagCount = eNum(rd, 'Agent - Red flag count');
  const agentRedFlags = e(rd, 'Agent - Red flags');
  const agentVerdictReasoning = e(rd, 'Agent - Final verdict (reasoning)');
  const eduVerification = e(rd, 'Agent - Education verification');
  const eduReasoning = e(rd, 'Agent - Education verification (reasoning)');
  const workVerification = e(rd, 'Agent - Work verification');
  const workReasoning = e(rd, 'Agent - Work verification (reasoning)');
  const githubReasoning = e(rd, 'Agent - GitHub/Portfolio verification (reasoning)')
    || e(rd, 'Agent - Publication verification (reasoning)');
  const githubVerification = e(rd, 'Agent - GitHub/Portfolio verification')
    || e(rd, 'Agent - Publications listed on resume verification')
    || e(rd, 'Agent - BINARY: Is public teaching/research profile for the same person?');
  const disqualifiers = e(rd, 'DISQUALIFIERS');
  const disqualifiersReasoning = e(rd, 'DISQUALIFIERS REASONING');
  const agentSources = e(rd, 'Agent - Sources');
  const justificationParagraph = e(rd, 'Agent - Justification paragraph');
  const fraudSource = (e(rd, 'Fraud check source') || '').toLowerCase();

  const hasDisqualifiers = isYes(disqualifiers);
  const agentFlagsPass = agentRedFlagCount !== null && agentRedFlagCount === 0;
  const agentVerdictReal = agentFinalVerdict.toUpperCase() === 'REAL';

  // Tri-state classifier: YES → pass (true), MISMATCH → fail (false),
  // anything else (CAN'T BE FOUND / empty / NOT SURE) → 'NOT_FOUND'.
  const classifyAgentSub = (v: string): TriPassState => {
    const u = v.toUpperCase();
    if (u === 'YES') return true;
    if (u === 'MISMATCH') return false;
    return 'NOT_FOUND';
  };
  const eduState = classifyAgentSub(eduVerification);
  const workState = classifyAgentSub(workVerification);
  const githubState = classifyAgentSub(githubVerification);
  const disqState: TriPassState = disqualifiers === '' ? 'NOT_FOUND' : !hasDisqualifiers;
  const agentVerdictState: TriPassState = (() => {
    const u = agentFinalVerdict.toUpperCase();
    if (u === 'REAL') return true;
    if (u === 'FAKE') return false;
    return 'NOT_FOUND';
  })();

  const publicSubStates: TriPassState[] = [eduState, workState, githubState, disqState, agentVerdictState];
  // 4-state column rollup matching backend:
  //   NO if any sub is a contradiction; YES if all subs YES; NOT_FOUND if all subs Not Found;
  //   else PARTIALLY_FOUND (mix of YES and NOT_FOUND, no NO).
  const publicPass: TriPassState = publicSubStates.includes(false)
    ? false
    : publicSubStates.every((s) => s === true)
      ? true
      : publicSubStates.every((s) => s === 'NOT_FOUND')
        ? 'NOT_FOUND'
        : 'PARTIALLY_FOUND';

  const publicSubChecks = [
    { label: 'Education', v: eduVerification },
    { label: 'Work History', v: workVerification },
    { label: isAcademic ? 'Publications / Research' : domain.toUpperCase().includes('FINANCE') ? 'Certifications' : 'GitHub / Domain Evidence', v: githubVerification },
    { label: 'Disqualifiers', v: hasDisqualifiers ? 'MISMATCH' : 'YES' },
  ];
  const publicReviewCount = publicSubChecks.filter((c) => {
    const upper = c.v.toUpperCase();
    return upper === 'NO' || upper === 'MISMATCH' || upper === 'FAKE';
  }).length + (!agentVerdictReal ? 1 : 0) + (!agentFlagsPass ? 1 : 0);

  const parsedSources = agentSources
    ? agentSources.split('|').map((s) => {
        const trimmed = s.trim();
        const urlMatch = trimmed.match(/(https?:\/\/[^\s]+)/);
        const label = trimmed.replace(urlMatch?.[0] ?? '', '').replace(/:\s*$/, '').replace(/\s*—\s*$/, '').trim();
        return { label: label || urlMatch?.[0] || '', url: urlMatch?.[0] || '' };
      }).filter((s) => s.label)
    : [];

  // ── 3. ID Verification ─────────────────────────────────────────────
  const linkedinLink = e(rd, 'Linkedin link');
  const identityViaLI = e(rd, 'Identity verfied via LinkedIn');
  const workViaLI = e(rd, 'Work verfied via LinkedIn');
  const educationViaLI = e(rd, 'Education verified via LinkedIn');
  // Kept identical to backend deriveLinkedinVerified: ID passes iff any of the
  // three specific LinkedIn signals is verified (non-empty and not 'No').
  const idPass = liIsVerified(identityViaLI) || liIsVerified(workViaLI) || liIsVerified(educationViaLI);

  // Account age comes from the fraud-check payload itself (`Linkedin Join date`,
  // YYYY-MM-DD). Previously it was read off the talent record via
  // getDeveloperDetails, which carries a joinedDate even when the fraud check
  // resolved no LinkedIn profile — rendering "No LinkedIn profile found ·
  // Joined June 2015". Keep every LinkedIn signal in this modal on one source.
  const linkedinJoinedDate = e(rd, 'Linkedin Join date') || null;

  const idSubSignals = [
    { label: 'Identity Match', value: identityViaLI },
    { label: 'Work Signal', value: workViaLI },
    { label: 'Education Signal', value: educationViaLI },
  ];
  const idReviewCount = idSubSignals.filter((s) => s.value && !liIsVerified(s.value)).length;

  // ── Final decision ─────────────────────────────────────────────────
  const publicPassStrict = publicPass === true;
  const isFinalYes = resumePass && publicPassStrict && idPass;

  const unresolvedCount = resumeReviewCount + publicReviewCount + idReviewCount;

  // ── Verdict text: first sentence of justification paragraph ────────
  const verdictText = (() => {
    if (justificationParagraph) {
      const firstSentence = justificationParagraph.match(/^[^.!?]+[.!?]/);
      return firstSentence ? firstSentence[0] : justificationParagraph.slice(0, 200);
    }
    if (isFinalYes) return 'All major claims verified. Candidate passes all automated checks.';
    const failed = [!resumePass && 'resume review', !publicPassStrict && 'public source check', !idPass && 'ID verification'].filter(Boolean);
    return `Candidate failed ${failed.join(', ')}.`;
  })();

  // ── Review Notes & Tags (persisted to MySQL) ───────────────────────
  const [reviewNotes, setReviewNotes] = useState(profile.reviewNotes || '');
  const [reviewTags, setReviewTags] = useState<string[]>(profile.reviewTags || []);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  const tagInputRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Reset when navigating to different candidate — flush pending save first
  const prevProfileId = useRef(profile.id);
  if (prevProfileId.current !== profile.id) {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = undefined; }
    prevProfileId.current = profile.id;
    setReviewNotes(profile.reviewNotes || '');
    setReviewTags(profile.reviewTags || []);
    setTagInput('');
    setShowTagInput(false);
  }

  const persistReview = useCallback((notes: string, tags: string[]) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const notesVal = notes || null;
      const tagsVal = tags.length > 0 ? tags : null;
      updateCandidateReview(profile.id, { reviewNotes: notesVal, reviewTags: tagsVal })
        .then(() => {
          (profile as any).reviewNotes = notesVal;
          (profile as any).reviewTags = tagsVal;
        })
        .catch(() => {});
    }, 800);
  }, [profile]);

  const handleNotesChange = (val: string) => {
    setReviewNotes(val);
    persistReview(val, reviewTags);
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || reviewTags.includes(tag)) { setTagInput(''); return; }
    const next = [...reviewTags, tag];
    setReviewTags(next);
    setTagInput('');
    setShowTagInput(false);
    persistReview(reviewNotes, next);
  };

  const removeTag = (tag: string) => {
    const next = reviewTags.filter((t) => t !== tag);
    setReviewTags(next);
    persistReview(reviewNotes, next);
  };

  useEffect(() => { if (showTagInput) tagInputRef.current?.focus(); }, [showTagInput]);

  // ── Render ─────────────────────────────────────────────────────────

  const content = (
    <div className={embedded ? 'flex flex-1 flex-col min-h-0' : 'flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl'} onClick={embedded ? undefined : (ev) => ev.stopPropagation()}>

      {!embedded && (
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-xl font-bold tracking-tight text-gray-900">{name || profile.talentId}</h2>
                {name && <span className="text-xs font-semibold text-gray-400">{profile.talentId}</span>}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-xs font-semibold leading-none bg-gray-100 text-gray-700 border-gray-200">{domain}</span>
              </div>
            </div>
            <button type="button" aria-label="Close" onClick={onClose} className="rounded-lg p-2 transition-colors hover:bg-gray-100">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5">

        {/* ── Verdict Banner ── */}
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Verdict</div>
              <div className="mt-2">
                <RichText text={justificationParagraph || verdictText} className="text-gray-700" maxLength={120} />
              </div>
            </div>
            <div>
              <div className="px-3 py-0 text-right">
                <div className="inline-block text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Recommendation</div>
                  <div className="mt-2 flex justify-center">
                    <StatusBadge value={isFinalYes ? 'YES' : 'NO'} yesLabel="Approve" noLabel="Reject" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Main 2-col layout ── */}
        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">

          {/* ── Left: Fraud Check Gates ── */}
          <div key={profile.id} className="space-y-4">
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900">Fraud Check</h3>
                  {fraudSource && (
                    <span
                      title={fraudSource === 'matching'
                        ? 'Result from the Matching (tpm) fraud-check API'
                        : 'Result from the local fraud-check pipeline (Matching API unavailable)'}
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        fraudSource === 'matching'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {fraudSource === 'matching' ? 'Matching' : 'Local'}
                    </span>
                  )}
                </div>
                <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                  {unresolvedCount > 0 ? `${unresolvedCount} unresolved signal${unresolvedCount !== 1 ? 's' : ''}` : 'No unresolved signals'}
                </span>
              </div>

              <div className="space-y-3 p-4">

                {/* ── Internal Resume Review ── */}
                <GateCard
                  icon={<FileText className="h-4 w-4" />}
                  title="Internal Resume Review"
                  passed={resumePass}
                  summary={`Score ${resumeScore !== null ? `${resumeScore}/100` : (resumeScoreCategory ?? '?')} · ${redFlagCount ?? '?'} resume flags`}
                  reviewCount={resumeReviewCount}
                  defaultOpen={!resumePass}
                >
                  <div className="space-y-3">
                    <SubCard title="Resume Score" passed={resumeScorePass}>
                      {resumeScore !== null ? (
                        <div className="mb-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{resumeScore}/100</span>
                            <span className={resumeScorePass ? 'text-green-600' : 'text-red-600'}>{resumeScorePass ? '≥ 60' : '< 60'}</span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-gray-200">
                            <div className={`h-1.5 rounded-full ${resumeScore >= 80 ? 'bg-green-500' : resumeScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${resumeScore}%` }} />
                          </div>
                        </div>
                      ) : resumeScoreCategory ? (
                        <div className="mb-2 flex items-center justify-between text-xs">
                          <span className="font-semibold text-gray-700">{resumeScoreCategory}</span>
                          <span className={resumeScorePass ? 'text-green-600' : 'text-red-600'}>{resumeScorePass ? 'Pass' : 'Fail'}</span>
                        </div>
                      ) : null}
                      {resumeScoreReasoning
                        ? <RichText text={resumeScoreReasoning} className="text-gray-500 text-sm" />
                        : <p className="text-sm text-gray-500">No reasoning provided.</p>}
                    </SubCard>

                    {(redFlagCount !== null && redFlagCount > 0) && (
                      <SubCard title="Red Flags" passed={false}>
                        {redFlagReasoning
                          ? <RichText text={redFlagReasoning} className="text-red-700 text-sm" />
                          : <p className="text-sm text-gray-500">{redFlagCount} flag(s) detected.</p>}
                      </SubCard>
                    )}

                    {localFlagsPass && (
                      <SubCard title="Red Flags" passed={true}>
                        <p className="text-sm text-gray-500">No red flags detected.</p>
                      </SubCard>
                    )}
                  </div>
                </GateCard>

                {/* ── Public Source Check ── */}
                <GateCard
                  icon={<CircleCheckBig className="h-4 w-4" />}
                  title="Public Source Check"
                  passed={publicPass}
                  summary={`${agentRedFlagCount ?? 0} total flags · Disqualifiers ${hasDisqualifiers ? 'found' : 'clear'}`}
                  reviewCount={publicReviewCount}
                  defaultOpen={!publicPass}
                >
                  <div className="space-y-3">
                    {/* Red flags banner */}
                    {(agentRedFlagCount ?? 0) > 0 && agentRedFlags && (
                      <div className="rounded-lg border border-red-100 bg-red-50/50 p-3 space-y-1">
                        {agentRedFlags.split('\n').filter(Boolean).map((flag, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm leading-5">
                            <SignalDot color="red" />
                            <span className="text-red-700">{flag.replace(/^[-•*]\s*/, '')}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <SubCard title="Agent Verdict" passed={agentVerdictState}>
                      {agentVerdictReasoning
                        ? <RichText text={agentVerdictReasoning} className="text-gray-500 text-sm" maxLength={100} />
                        : <p className="text-sm text-gray-500">Verdict: {agentFinalVerdict || 'N/A'}</p>}
                    </SubCard>

                    <SubCard title="Education" passed={eduState}>
                      {eduReasoning
                        ? <RichText text={eduReasoning} className="text-gray-500 text-sm" maxLength={100} />
                        : <p className="text-sm text-gray-500">{eduVerification || 'N/A'}</p>}
                    </SubCard>

                    <SubCard title="Work History" passed={workState}>
                      {workReasoning
                        ? <RichText text={workReasoning} className="text-gray-500 text-sm" maxLength={100} />
                        : <p className="text-sm text-gray-500">{workVerification || 'N/A'}</p>}
                    </SubCard>

                    <SubCard title={isAcademic ? 'Publications / Research' : domain.toUpperCase().includes('FINANCE') ? 'Certifications' : 'GitHub / Domain Evidence'} passed={githubState}>
                      {githubReasoning
                        ? <RichText text={githubReasoning} className="text-gray-500 text-sm" maxLength={100} />
                        : <p className="text-sm text-gray-500">{githubVerification || 'N/A'}</p>}
                    </SubCard>

                    <SubCard title="Disqualifiers" passed={disqState}>
                      {disqualifiersReasoning
                        ? <RichText text={disqualifiersReasoning} className="text-gray-500 text-sm" maxLength={100} />
                        : <p className="text-sm text-gray-500">{disqState === 'NOT_FOUND' ? 'N/A' : hasDisqualifiers ? 'Disqualifiers found' : 'No disqualifiers found.'}</p>}
                    </SubCard>

                    {/* Sources — collapsible */}
                    <SourcesCard sources={parsedSources} />
                  </div>
                </GateCard>

                {/* ── ID Verification ── */}
                <GateCard
                  icon={<UserPlus className="h-4 w-4" />}
                  title="ID Verification"
                  passed={idPass}
                  summary={`LinkedIn ${idPass ? 'verified' : linkedinLink ? 'not verified' : 'not found'}${linkedinJoinedDate ? ` · Account age ${linkedInAccountAgeYears(linkedinJoinedDate)}y` : ''}`}
                  reviewCount={idReviewCount}
                  defaultOpen={!idPass}
                >
                  <div className="space-y-3">
                    {/* LinkedIn link + account age */}
                    <div className="flex flex-wrap items-center gap-3">
                      {linkedinLink ? (
                        <a href={linkedinLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline">
                          Open LinkedIn <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">No LinkedIn profile found</span>
                      )}
                      {linkedinJoinedDate && (
                        <span className={`text-xs ${linkedInAccountAgeYears(linkedinJoinedDate) >= 10 ? 'text-green-600' : 'text-gray-500'}`}>
                          {formatLinkedInAccountAge(linkedinJoinedDate)}
                        </span>
                      )}
                    </div>

                    {/* Sub-signals */}
                    {idSubSignals.map((item) => (
                      <SubCard key={item.label} title={item.label} passed={liIsVerified(item.value)}>
                        {liIsVerified(item.value) && item.value.toUpperCase() !== 'YES'
                          ? <p className="text-sm text-gray-500">{item.value}</p>
                          : <p className="text-sm text-gray-500">{liIsVerified(item.value) ? 'Verified' : 'Not verified'}</p>}
                      </SubCard>
                    ))}
                  </div>
                </GateCard>

              </div>
            </section>
          </div>

          {/* ── Right: Sidebar ── */}
          <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
            {/* Review Notes */}
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900">Review Notes</h3>
              </div>
              <textarea
                rows={6}
                placeholder="Add internal review notes..."
                value={reviewNotes}
                onChange={(ev) => handleNotesChange(ev.target.value)}
                className="w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm leading-6 text-gray-700 outline-none transition-colors focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
            </section>

            {/* Review Tags */}
            <section className="relative rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900">Review Tags</h3>
                </div>
                <button
                  type="button"
                  title="Add review tag"
                  onClick={() => setShowTagInput(true)}
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {reviewTags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="rounded-full text-blue-400 transition-colors hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {reviewTags.length === 0 && !showTagInput && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                    <Tag className="h-3 w-3" /> No tags
                  </span>
                )}
              </div>
              {showTagInput && (
                <div className="mt-2 flex gap-1.5">
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={tagInput}
                    onChange={(ev) => setTagInput(ev.target.value)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') addTag(); if (ev.key === 'Escape') { setShowTagInput(false); setTagInput(''); } }}
                    placeholder="Tag name..."
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                  <button type="button" onClick={addTag} disabled={!tagInput.trim()} className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">Add</button>
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-end gap-3">
        <button onClick={onRemove} disabled={actionLoading} className="flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50">
          {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleX className="h-4 w-4" />}
          Reject
        </button>
        <button onClick={onApprove} disabled={actionLoading} className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50">
          {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheck className="h-4 w-4" />}
          Approve
        </button>
      </div>
    </div>
  );

  if (embedded) return content;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      {content}
    </div>,
    document.body,
  );
}
